import { z } from "zod";
import { creditAvailable, creditLimit } from "@cwb/core";
import type {
  ArmyMovement,
  BrokerScore,
  BrokerState,
  FacilityKind,
  FrontState,
  GoodType,
  MarketOrder,
  MarketPriceIndex,
  NationAgentView,
  NationId,
  NationalPolicy,
  ProvinceState,
  ProvinceType,
  SessionPhase,
  Shipment,
  StrategicMode,
  SupportRequest,
  Vec2,
  WorldState,
} from "@cwb/core";

/**
 * Wire projection of the world.
 *
 * Province geometry never changes during a run, so it is sent in snapshots and
 * the 5 Hz stream (`world.patch`) carries only the volatile half. The fleet
 * position is derived from the volatile tick; `brokerHub` below is its position
 * at the moment this map projection was made.
 */

// ---------------------------------------------------------------------------
// static half
// ---------------------------------------------------------------------------

export interface MapProvince {
  id: string;
  name: string;
  type: ProvinceType;
  polygon: Vec2[];
  center: Vec2;
  neighborIds: string[];
  originalOwnerNationId: NationId;
}

export interface MapNation {
  id: NationId;
  name: string;
  color: number;
  accentColor: number;
  personality: string;
  capitalProvinceId: string;
  depotProvinceId: string;
}

export interface MapPayload {
  mapBounds: { width: number; height: number };
  /** Broker fleet position at projection time; subsequent positions derive from the tick. */
  brokerHub: Vec2;
  provinces: MapProvince[];
  nations: MapNation[];
}

// ---------------------------------------------------------------------------
// volatile half
// ---------------------------------------------------------------------------

export interface DynamicProvince {
  id: string;
  ownerNationId: NationId;
  fortification: number;
  supply: number;
  /** Garrison and sellable stock in one number. */
  weapons: number;
  resources: number;
  resourceFacilities: number;
  weaponFactories: number;
  houses: number;
  lastCapturedTick: number;
}

export interface DynamicNation {
  id: NationId;
  treasury: number;
  atWarWith: NationId[];
  peaceOffersTo: NationId[];
  /** Nations this one will not fight. Sorted, so the wire order is stable. */
  alliedWith: NationId[];
  allianceOffersTo: NationId[];
  strategicMode: StrategicMode;
  morale: number;
  buildPriority: Record<FacilityKind, number>;
  eliminated: boolean;
  provinceCount: number;
  /**
   * Summed over the nation's provinces here rather than in the browser: the UI
   * shows these on every frame and the server already walks the list.
   */
  totalWeapons: number;
  totalResources: number;

  /** Owed to the broker — principal plus the interest already rolled into it. */
  debtToBroker: number;
  /** Interest charged over the run, kept apart so the ledger can show the cost. */
  debtInterestAccrued: number;
  /**
   * The ceiling and the headroom are derived from territory, so the browser
   * cannot compute them from the fields above. They are sent rather than
   * re-derived because the trade panel needs both on every demand row.
   */
  creditLimit: number;
  creditAvailable: number;

  /**
   * What this nation's king last decided, and why in its own words. Null until
   * it has spoken. The reading is the reason this is on the wire at all — a
   * stance the player cannot read is just a number.
   */
  policy: NationalPolicy | null;
  /** 0..100 per nation — how far this one likes each of the others. */
  regardToward: Record<NationId, number>;
}

export interface DynamicState {
  tick: number;
  tickRateHz: number;
  phase: SessionPhase;
  endReason: string | null;
  provinces: DynamicProvince[];
  nations: DynamicNation[];
  fronts: FrontState[];
  movements: ArmyMovement[];
  shipments: Shipment[];
  marketOrders: MarketOrder[];
  priceIndex: Record<GoodType, MarketPriceIndex>;
  broker: BrokerState;
  /** Open and recently-answered calls for help, oldest first. */
  supportRequests: SupportRequest[];
}

/** Player-selected authoritative simulation rate; zero means paused. */
export const SessionSpeedSchema = z.union([
  z.literal(0),
  z.literal(1),
  z.literal(2),
  z.literal(4),
]);
export type SessionSpeed = z.infer<typeof SessionSpeedSchema>;

/**
 * The server-authoritative simulation-time window for one broker run.
 *
 * Both values are deterministic ticks. The server sends the remaining value
 * explicitly so clients do not duplicate the end-condition arithmetic.
 */
export interface RoundClock {
  durationTicks: number;
  remainingTicks: number;
}

export interface WorldPatch {
  sessionId: string;
  simulationSpeed: SessionSpeed;
  roundClock: RoundClock;
  state: DynamicState;
  agents: NationAgentView[];
  score: BrokerScore;
}

export interface WorldSnapshot extends WorldPatch {
  runId: string;
  seed: string;
  aiMode: string;
  map: MapPayload;
}

// ---------------------------------------------------------------------------
// projection
// ---------------------------------------------------------------------------

export function projectMap(world: WorldState): MapPayload {
  return {
    mapBounds: { ...world.mapBounds },
    brokerHub: { ...world.brokerHub },
    provinces: world.provinceOrder.map((id) => {
      const province = world.provinces[id] as ProvinceState;
      return {
        id: province.id,
        name: province.name,
        type: province.type,
        polygon: province.polygon.map((p) => ({ x: p.x, y: p.y })),
        center: { ...province.center },
        neighborIds: [...province.neighborIds],
        originalOwnerNationId: province.originalOwnerNationId,
      };
    }),
    nations: world.nationOrder.map((id) => {
      const nation = world.nations[id];
      return {
        id: nation.id,
        name: nation.name,
        color: nation.color,
        accentColor: nation.accentColor,
        personality: nation.personality,
        capitalProvinceId: nation.capitalProvinceId,
        depotProvinceId: nation.depotProvinceId,
      };
    }),
  };
}

export function projectState(world: WorldState): DynamicState {
  const provinceCounts = {} as Record<NationId, number>;
  const weaponTotals = {} as Record<NationId, number>;
  const resourceTotals = {} as Record<NationId, number>;
  for (const id of world.nationOrder) {
    provinceCounts[id] = 0;
    weaponTotals[id] = 0;
    resourceTotals[id] = 0;
  }

  const provinces: DynamicProvince[] = world.provinceOrder.map((id) => {
    const province = world.provinces[id] as ProvinceState;
    provinceCounts[province.ownerNationId] += 1;
    weaponTotals[province.ownerNationId] += province.weapons;
    resourceTotals[province.ownerNationId] += province.resources;
    return {
      id: province.id,
      ownerNationId: province.ownerNationId,
      fortification: round(province.fortification, 3),
      supply: round(province.supply, 3),
      weapons: round(province.weapons, 2),
      resources: round(province.resources, 2),
      resourceFacilities: province.resourceFacilities,
      weaponFactories: province.weaponFactories,
      houses: province.houses,
      lastCapturedTick: province.lastCapturedTick,
    };
  });

  return {
    tick: world.tick,
    tickRateHz: world.tickRateHz,
    phase: world.phase,
    endReason: world.endReason,
    provinces,
    nations: world.nationOrder.map((id) => {
      const nation = world.nations[id];
      return {
        id: nation.id,
        treasury: Math.round(nation.treasury),
        atWarWith: [...nation.atWarWith],
        peaceOffersTo: [...nation.peaceOffersTo],
        alliedWith: [...nation.alliedWith],
        allianceOffersTo: [...nation.allianceOffersTo],
        strategicMode: nation.strategicMode,
        morale: round(nation.morale, 3),
        buildPriority: {
          resource: round(nation.buildPriority.resource, 3),
          weapon: round(nation.buildPriority.weapon, 3),
          housing: round(nation.buildPriority.housing, 3),
        },
        eliminated: nation.eliminated,
        provinceCount: provinceCounts[nation.id],
        totalWeapons: round(weaponTotals[nation.id], 2),
        totalResources: round(resourceTotals[nation.id], 2),
        debtToBroker: Math.round(nation.debtToBroker),
        debtInterestAccrued: Math.round(nation.debtInterestAccrued),
        creditLimit: Math.round(creditLimit(world, nation.id)),
        creditAvailable: Math.round(creditAvailable(world, nation.id)),
        policy: nation.policy ? { ...nation.policy } : null,
        regardToward: { ...nation.regardToward },
      };
    }),
    fronts: world.frontOrder.map((id) => ({ ...(world.fronts[id] as FrontState) })),
    movements: world.movements.map((m) => ({ ...m, position: { ...m.position }, path: [...m.path] })),
    shipments: world.shipments.map((s) => ({
      ...s,
      position: { ...s.position },
      fromPoint: { ...s.fromPoint },
      toPoint: { ...s.toPoint },
    })),
    marketOrders: world.marketOrders.map((o) => ({ ...o })),
    priceIndex: {
      weapons: { ...world.priceIndex.weapons },
      resources: { ...world.priceIndex.resources },
    },
    broker: { ...world.broker, warehouse: { ...world.broker.warehouse } },
    supportRequests: world.supportRequests.map((r) => ({ ...r })),
  };
}

function round(value: number, digits: number): number {
  const f = 10 ** digits;
  return Math.round(value * f) / f;
}

// ---------------------------------------------------------------------------
// runtime schemas
//
// The snapshot stream is server-authored, so it is not re-validated on every
// broadcast. These schemas exist so tests (and any future third-party client)
// can assert the wire shape without hand-rolling it.
// ---------------------------------------------------------------------------

export const Vec2Schema = z.object({ x: z.number(), y: z.number() });

export const NationIdSchema = z.enum(["qin", "zhao", "wei", "han", "chu", "qi", "yan"]);
export const GoodTypeSchema = z.enum(["weapons", "resources"]);
export const FacilityKindSchema = z.enum(["resource", "weapon", "housing"]);

export const BuildPrioritySchema = z.object({
  resource: z.number(),
  weapon: z.number(),
  housing: z.number(),
});

export const DynamicProvinceSchema = z.object({
  id: z.string(),
  ownerNationId: NationIdSchema,
  fortification: z.number(),
  supply: z.number(),
  weapons: z.number(),
  resources: z.number(),
  resourceFacilities: z.number(),
  weaponFactories: z.number(),
  houses: z.number(),
  lastCapturedTick: z.number(),
});

/**
 * The standing decision of this nation's king.
 *
 * `reading` is the king's own words and it is the reason this crosses the wire
 * at all: a stance the player cannot read is a number, and the point of asking a
 * model rather than a lookup table is that it can say *why*. Already scrubbed
 * server-side — raw model output never reaches a browser.
 */
export const NationPolicySchema = z.object({
  kind: z.enum([
    "SEEK_CEASEFIRE",
    "REQUEST_SUPPORT",
    "SEEK_ALLIANCE",
    "WAGE_WAR",
    "WITHDRAW_FRONT",
    "BREAK_PACT",
    "LAST_STAND",
  ]),
  targetNationId: NationIdSchema.nullable(),
  reading: z.string(),
  decidedTick: z.number(),
});

/** A call for help and its answer, as the world log and dossier show them. */
export const SupportRequestViewSchema = z.object({
  id: z.string(),
  fromNationId: NationIdSchema,
  toNationId: NationIdSchema,
  againstNationId: NationIdSchema.nullable(),
  answer: z.enum(["TROOPS", "MATERIEL", "REFUSED"]).nullable(),
});

export const DynamicNationSchema = z.object({
  id: NationIdSchema,
  treasury: z.number(),
  atWarWith: z.array(NationIdSchema),
  peaceOffersTo: z.array(NationIdSchema),
  alliedWith: z.array(NationIdSchema),
  allianceOffersTo: z.array(NationIdSchema),
  strategicMode: z.enum(["EXPAND", "DEFEND", "RECOVER", "TRADE", "SEEK_PEACE"]),
  morale: z.number(),
  buildPriority: BuildPrioritySchema,
  eliminated: z.boolean(),
  provinceCount: z.number(),
  totalWeapons: z.number(),
  totalResources: z.number(),
  debtToBroker: z.number(),
  debtInterestAccrued: z.number(),
  creditLimit: z.number(),
  creditAvailable: z.number(),
  /** Null until this nation's king has spoken for the first time. */
  policy: NationPolicySchema.nullable(),
  /** 0..100 per nation — how far this one likes each of the others. */
  regardToward: z.record(NationIdSchema, z.number()),
});

export const GoodStockSchema = z.object({
  weapons: z.number(),
  resources: z.number(),
});

/**
 * The broker's own books. `totalLent` and `badDebt` are the two halves of the
 * credit story — what went out on the book, and what will never come back — so
 * the wire carries them even though only the end card prints `badDebt`.
 */
export const BrokerStateSchema = z.object({
  cash: z.number(),
  warehouse: GoodStockSchema,
  totalSpent: z.number(),
  totalEarned: z.number(),
  totalLent: z.number(),
  interestEarned: z.number(),
  badDebt: z.number(),
  inventoryCost: GoodStockSchema,
});

export const BrokerScoreSchema = z.object({
  cash: z.number(),
  inventoryValue: z.number(),
  receivableCost: z.number(),
  receivables: z.number(),
  badDebt: z.number(),
  tradingProfit: z.number(),
  netWorth: z.number(),
  finalScore: z.number(),
});

export const MarketOrderSchema = z.object({
  id: z.string(),
  nationId: NationIdSchema,
  side: z.enum(["BUY", "SELL"]),
  goodType: GoodTypeSchema,
  quantity: z.number(),
  remainingQuantity: z.number(),
  unitPrice: z.number(),
  createdTick: z.number(),
  expiresAtTick: z.number(),
  reason: z.string(),
});
